import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchPageById, createPage, updatePage, fetchPageByName } from '../../../store/slices/cmsAdminSlice';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import toast from 'react-hot-toast';
import { CMS_PAGE_OPTIONS } from '../../../constants/cmsPages';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import cmsService from '../../../services/cmsService';
import {
  attachCmsUploadAdapter,
  validateCmsEditorImageFile
} from '../../../utils/cmsEditorImageUpload';

const CMSEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { currentPage, loading } = useAppSelector((state) => state.cmsAdmin);
  const { user } = useAppSelector((state) => state.auth);
  const { perms } = useAdminPermissions();
  const editorRef = useRef(null);

  const isFullAdmin = !user || user.user_type === 'Admin';
  const canUploadImages = isFullAdmin || perms.cms?.edit === true;

  const [formData, setFormData] = useState({
    page_name: '',
    title: '',
    keyword: '',
    meta_title: '',
    meta_description: '',
    og_title: '',
    og_description: '',
    og_image: '',
    og_type: 'website',
    status: 'Published',
    page_content: '',
    created_by: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [prefillLoading, setPrefillLoading] = useState(false);

  const uploadEditorImage = useCallback(async (file) => {
    if (!canUploadImages) {
      toast.error('You do not have permission to upload images.');
      throw new Error('Permission denied');
    }
    const validationError = validateCmsEditorImageFile(file);
    if (validationError) {
      toast.error(validationError);
      throw new Error(validationError);
    }
    const response = await cmsService.uploadEditorImage(file);
    if (!response?.url) {
      throw new Error(response?.message || 'Upload failed');
    }
    return response.url;
  }, [canUploadImages]);

  const handleEditorReady = useCallback((editor) => {
    editorRef.current = editor;
    if (canUploadImages) {
      attachCmsUploadAdapter(editor, uploadEditorImage);
    }
  }, [canUploadImages, uploadEditorImage]);

  useEffect(() => {
    if (id && id !== 'null' && !isNaN(Number(id))) {
      dispatch(fetchPageById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (user && !id) {
      const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Admin';
      setFormData(prev => ({
        ...prev,
        created_by: userName
      }));
    }
  }, [user, id]);

  useEffect(() => {
    if (currentPage && id) {
      setFormData({
        page_name: currentPage.page_name || '',
        title: currentPage.title || '',
        keyword: currentPage.keyword || '',
        meta_title: currentPage.meta_title || '',
        meta_description: currentPage.meta_description || '',
        og_title: currentPage.og_title || '',
        og_description: currentPage.og_description || '',
        og_image: currentPage.og_image || '',
        og_type: currentPage.og_type || 'website',
        status: currentPage.status || 'Published',
        page_content: currentPage.page_content || '',
        created_by: currentPage.created_by || ''
      });
    }
  }, [currentPage, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'page_name' && { title: value })
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (name === 'page_name' && !id && value) {
      setPrefillLoading(true);
      dispatch(fetchPageByName(value))
        .unwrap()
        .then((data) => {
          if (data) {
            setFormData(prev => ({
              ...prev,
              page_name: value,
              title: value,
              keyword: data.keyword || prev.keyword,
              meta_title: data.meta_title || prev.meta_title,
              meta_description: data.meta_description || prev.meta_description,
              og_title: data.og_title || prev.og_title,
              og_description: data.og_description || prev.og_description,
              og_image: data.og_image || prev.og_image,
              og_type: data.og_type || prev.og_type,
              status: data.status || prev.status,
              page_content: data.page_content || ''
            }));
          }
        })
        .catch(() => {})
        .finally(() => {
          setPrefillLoading(false);
        });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.page_name.trim()) {
      newErrors.page_name = 'Page name is required';
    }

    if (!formData.page_content.trim()) {
      newErrors.page_content = 'Page content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors');
      return;
    }

    setIsSubmitting(true);

    try {
      if (id) {
        await dispatch(updatePage({ id, data: formData })).unwrap();
        toast.success('Page updated successfully!');
      } else {
        await dispatch(createPage(formData)).unwrap();
        toast.success('Page created successfully!');
      }

      navigate('/admin/cms');
    } catch (error) {
      toast.error(error || 'Failed to save page');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && id) {
    return (
      <div className="p-4 flex justify-center items-center h-full">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4 rounded-full"></div>
          <p>Loading page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 w-full h-full flex flex-col bg-gray-50">
      <div className="flex justify-between items-center mb-6 bg-white p-4 rounded shadow-sm">
        <h1 className="text-2xl font-bold">
          {id ? 'Edit Page' : 'Create Page'}
        </h1>

        <button
          type="button"
          onClick={() => navigate('/admin/cms')}
          className="px-4 py-2 bg-yellow-400 rounded"
        >
          Back
        </button>
      </div>

      <div className="bg-white rounded shadow-sm flex-1">
        <form onSubmit={handleSubmit} className="p-6 flex flex-col h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block mb-2 font-semibold">
                Page Name *
              </label>
              {id ? (
                <input
                  type="text"
                  name="page_name"
                  value={formData.page_name}
                  readOnly
                  className="w-full border px-4 py-2 rounded bg-gray-100 cursor-not-allowed"
                />
              ) : (
                <select
                  name="page_name"
                  value={formData.page_name}
                  onChange={handleInputChange}
                  disabled={prefillLoading}
                  className="w-full border px-4 py-2 rounded disabled:opacity-60 disabled:cursor-wait"
                >
                  <option value="" disabled>Select Page</option>
                  {CMS_PAGE_OPTIONS.map((page) => (
                    <option key={page} value={page}>{page}</option>
                  ))}
                </select>
              )}
              {errors.page_name && <p className="text-red-500 mt-1">{errors.page_name}</p>}
            </div>

            <div>
              <label className="block mb-2 font-semibold">
                Meta Keyword
              </label>
              <input
                type="text"
                name="keyword"
                value={formData.keyword}
                onChange={handleInputChange}
                className="w-full border px-4 py-2 rounded"
                placeholder="private jet, empty leg"
              />
            </div>
          </div>

          <div className="mb-6 border border-gray-200 rounded p-4 bg-gray-50">
            <h2 className="text-lg font-semibold mb-4">SEO &amp; Open Graph</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 font-semibold">Meta Title</label>
                <input
                  type="text"
                  name="meta_title"
                  value={formData.meta_title}
                  onChange={handleInputChange}
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Overrides browser tab title"
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold">OG Type</label>
                <select
                  name="og_type"
                  value={formData.og_type}
                  onChange={handleInputChange}
                  className="w-full border px-4 py-2 rounded"
                >
                  <option value="website">website</option>
                  <option value="article">article</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block mb-2 font-semibold">Meta Description</label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Page-specific description for search engines"
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold">OG Title</label>
                <input
                  type="text"
                  name="og_title"
                  value={formData.og_title}
                  onChange={handleInputChange}
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Defaults to meta title"
                />
              </div>
              <div>
                <label className="block mb-2 font-semibold">OG Image URL</label>
                <input
                  type="text"
                  name="og_image"
                  value={formData.og_image}
                  onChange={handleInputChange}
                  className="w-full border px-4 py-2 rounded"
                  placeholder="https://… absolute image URL"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-2 font-semibold">OG Description</label>
                <textarea
                  name="og_description"
                  value={formData.og_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Defaults to meta description"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 w-1/2">
            <label className="block mb-2 font-semibold">
              Status *
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full border px-4 py-2 rounded"
            >
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>

          <div className="mb-6 flex-1">
            <label className="block mb-2 font-semibold">
              Content *
            </label>
            {!canUploadImages && (
              <p className="text-xs text-amber-700 mb-2">
                Image upload is not available for your role. You can still add images by URL only.
              </p>
            )}

            <div className={`border rounded relative ${errors.page_content ? 'border-red-500' : ''}`}>
              {prefillLoading && (
                <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded">
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <div className="animate-spin h-5 w-5 border-b-2 border-yellow-400 rounded-full"></div>
                    Loading existing content...
                  </div>
                </div>
              )}
              <CKEditor
                editor={ClassicEditor}
                data={formData.page_content}
                onReady={handleEditorReady}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setFormData(prev => ({
                    ...prev,
                    page_content: data
                  }));
                }}
              />
            </div>

            {errors.page_content && (
              <p className="text-red-500 mt-1">{errors.page_content}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/cms')}
              className="px-6 py-2 bg-gray-300 rounded"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-yellow-400 rounded"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMSEditor;
